import React, { useState, useEffect } from 'react';
import { Save, Eye, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IsolirPageEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [config, setConfig] = useState({
    title: '',
    heading: '',
    heading_size: '24',
    subtitle: '',
    company_name: '',
    company_phone: '',
    company_email: '',
    payment_info: '',
    payment_image_url: '',
    payment_image_size: '120',
    payment_link_url: '',
    payment_link_text: 'KLIK DISINI',
    primary_color: '#667eea',
    secondary_color: '#764ba2',
    logo_url: '',
    logo_size: '80'
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/isolir-page/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        console.log('[IsolirEditor] Fetched config:', data);
        // Merge with defaults to ensure all fields exist
        setConfig(prevConfig => ({
          ...prevConfig,
          title: data.title || '',
          heading: data.heading || '',
          heading_size: data.heading_size || '24',
          subtitle: data.subtitle || '',
          company_name: data.company_name || '',
          company_phone: data.company_phone || '',
          company_email: data.company_email || '',
          payment_info: data.payment_info || '',
          payment_image_url: data.payment_image_url || '',
          payment_image_size: data.payment_image_size || '120',
          payment_link_url: data.payment_link_url || '',
          payment_link_text: data.payment_link_text || 'KLIK DISINI',
          primary_color: data.primary_color || '#667eea',
          secondary_color: data.secondary_color || '#764ba2',
          logo_url: data.logo_url || '',
          logo_size: data.logo_size || '80'
        }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Gagal memuat konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipe file tidak didukung. Gunakan PNG, JPG, atau SVG');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File terlalu besar. Maksimal 2MB');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/v2/isolir-page/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const logoUrl = response.data.logo_url;
        setConfig({ ...config, logo_url: logoUrl });
        toast.success('Logo berhasil diupload!');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        toast.error(error.response?.data?.detail || 'Gagal upload logo');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePaymentImage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/v2/isolir-page/delete-payment-image`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setConfig({ ...config, payment_image_url: '' });
        toast.success('Gambar pembayaran berhasil dihapus');
      }
    } catch (error) {
      console.error('Delete payment image error:', error);
      toast.error('Gagal menghapus gambar');
    }
  };

  const handlePaymentImageUpload = async (event) => {
    console.log('[Payment Upload] Event triggered:', event);
    const file = event.target.files[0];
    console.log('[Payment Upload] Selected file:', file);
    if (!file) {
      console.log('[Payment Upload] No file selected');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format file tidak didukung. Gunakan PNG, JPG, atau GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingPayment(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/v2/isolir-page/upload-payment-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setConfig({ ...config, payment_image_url: response.data.payment_image_url });
        toast.success('Gambar pembayaran berhasil diupload!');
      }
    } catch (error) {
      console.error('Error uploading payment image:', error);
      toast.error(error.response?.data?.detail || 'Gagal upload gambar');
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }
      
      console.log('[IsolirEditor] Starting save operation...');
      console.log('[IsolirEditor] Config to save:', config);
      
      const response = await axios.put(
        `${API_URL}/api/v2/isolir-page/config`,
        config,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 seconds timeout
        }
      );
      
      console.log('[IsolirEditor] Save response:', response.data);
      
      if (response.data.success) {
        toast.success('✅ Halaman isolir berhasil diupdate!', {
          duration: 3000,
          position: 'top-center'
        });
        // Reload config to show saved data
        await fetchConfig();
      } else {
        toast.error('❌ Gagal menyimpan: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('[IsolirEditor] Error saving config:', error);
      console.error('[IsolirEditor] Error response:', error.response);
      console.error('[IsolirEditor] Error message:', error.message);
      
      // Handle token errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else if (error.response?.data?.detail?.includes('token')) {
        toast.error('Invalid session. Please login again.');
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error('⏱️ Request timeout. Silakan coba lagi.');
      } else if (!error.response) {
        toast.error('❌ Network error. Periksa koneksi internet Anda.');
      } else {
        toast.error('❌ ' + (error.response?.data?.detail || error.message || 'Gagal menyimpan konfigurasi'));
      }
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    // Open preview using backend endpoint
    // Get user's tenant_id and open tenant-specific preview
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 'default';
    window.open(`${API_URL}/api/public/isolir/${tenantId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300">Memuat konfigurasi...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-slate-100">Editor Halaman Isolir</h1>
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Customize halaman yang ditampilkan ke pelanggan yang diisolir
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={openPreview}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Simpan Perubahan
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 dark:bg-blue-900/30">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 dark:text-blue-400" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">Info</h3>
            <p className="text-xs text-blue-800">
              Halaman ini akan ditampilkan ke pelanggan yang diisolir (layanan ditangguhkan). 
              Pastikan informasi kontak dan payment sudah benar sebelum menyimpan.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Konten Halaman</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Title (Browser Tab)
                </label>
                <Input
                  value={config.title}
                  onChange={(e) => setConfig({ ...config, title: e.target.value })}
                  placeholder="Layanan Ditangguhkan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Heading Utama
                </label>
                <Input
                  value={config.heading}
                  onChange={(e) => setConfig({ ...config, heading: e.target.value })}
                  placeholder="Layanan Internet Anda Ditangguhkan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Ukuran Font Heading (px)
                </label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="16"
                    max="48"
                    value={config.heading_size || '24'}
                    onChange={(e) => setConfig({ ...config, heading_size: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min="16"
                    max="48"
                    value={config.heading_size || '24'}
                    onChange={(e) => setConfig({ ...config, heading_size: e.target.value })}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500 dark:text-slate-400">px</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Subtitle / Pesan
                </label>
                <textarea
                  value={config.subtitle}
                  onChange={(e) => setConfig({ ...config, subtitle: e.target.value })}
                  placeholder="Maaf, akses internet Anda sementara dinonaktifkan..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px] dark:border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Informasi Pembayaran
                </label>
                <textarea
                  value={config.payment_info}
                  onChange={(e) => setConfig({ ...config, payment_info: e.target.value })}
                  placeholder="Silakan lakukan pembayaran untuk mengaktifkan kembali layanan..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px] dark:border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Gambar Metode Pembayaran
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={config.payment_image_url}
                    onChange={(e) => setConfig({ ...config, payment_image_url: e.target.value })}
                    placeholder="URL gambar atau upload dari komputer"
                    className="flex-1"
                  />
                  <label className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md cursor-pointer hover:bg-green-700 transition-colors">
                    {uploadingPayment ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handlePaymentImageUpload}
                      className="hidden"
                      disabled={uploadingPayment}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Format: PNG, JPG, GIF, WebP (max 5MB)
                </p>
                {config.payment_image_url && (
                  <div className="mt-3 p-3 bg-gray-50 rounded border dark:bg-slate-900">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs text-gray-600 dark:text-slate-300">Preview:</p>
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, payment_image_url: '' })}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Hapus
                      </button>
                    </div>
                    <img 
                      src={config.payment_image_url.startsWith('http') ? config.payment_image_url : `${API_URL.replace('/api', '')}${config.payment_image_url}`}
                      alt="Payment Methods" 
                      style={{ maxHeight: `${config.payment_image_size || 120}px` }}
                      className="max-w-full object-contain mx-auto"
                      onError={(e) => { console.log('Payment image load error:', e.target.src); }}
                    />
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Ukuran Gambar Pembayaran (px)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="60"
                      max="200"
                      value={config.payment_image_size || '120'}
                      onChange={(e) => setConfig({ ...config, payment_image_size: e.target.value })}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700"
                    />
                    <Input
                      type="number"
                      min="60"
                      max="200"
                      value={config.payment_image_size || '120'}
                      onChange={(e) => setConfig({ ...config, payment_image_size: e.target.value })}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500 dark:text-slate-400">px</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Teks Link Pembayaran
                  </label>
                  <Input
                    value={config.payment_link_text}
                    onChange={(e) => setConfig({ ...config, payment_link_text: e.target.value })}
                    placeholder="KLIK DISINI"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    URL Link Pembayaran
                  </label>
                  <Input
                    value={config.payment_link_url}
                    onChange={(e) => setConfig({ ...config, payment_link_url: e.target.value })}
                    placeholder="https://payment.example.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informasi Kontak</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Nama Perusahaan
                </label>
                <Input
                  value={config.company_name}
                  onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                  placeholder="ISP Provider"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Nomor Telepon
                </label>
                <Input
                  value={config.company_phone}
                  onChange={(e) => setConfig({ ...config, company_phone: e.target.value })}
                  placeholder="0821-1234-5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Email
                </label>
                <Input
                  type="email"
                  value={config.company_email}
                  onChange={(e) => setConfig({ ...config, company_email: e.target.value })}
                  placeholder="support@isp.com"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Branding & Design</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Logo
                </label>
                
                <div className="mb-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      uploading ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin text-gray-500 dark:text-slate-400" />
                        <span className="text-sm text-gray-500 dark:text-slate-400">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-sm text-indigo-700 font-medium dark:text-indigo-300">Upload dari File</span>
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    PNG, JPG, SVG (Max 2MB)
                  </p>
                </div>

                <div className="relative">
                  <label className="block text-xs text-gray-500 mb-1 dark:text-slate-400">Atau masukkan URL:</label>
                  <Input
                    type="url"
                    value={config.logo_url}
                    onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                {config.logo_url && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg dark:bg-slate-900">
                    <p className="text-xs text-gray-600 mb-2 dark:text-slate-300">Preview Logo:</p>
                    <div className="flex items-center justify-center bg-white p-4 rounded border dark:bg-slate-800">
                      <img 
                        src={config.logo_url.startsWith('http') ? config.logo_url : `${API_URL.replace('/api', '')}${config.logo_url}`}
                        alt="Logo Preview" 
                        style={{ maxHeight: `${config.logo_size || 80}px` }}
                        className="max-w-full object-contain"
                        onError={(e) => {
                          console.log('Logo load error:', e.target.src);
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Ukuran Logo (px)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="40"
                      max="150"
                      value={config.logo_size || '80'}
                      onChange={(e) => setConfig({ ...config, logo_size: e.target.value })}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700"
                    />
                    <Input
                      type="number"
                      min="40"
                      max="150"
                      value={config.logo_size || '80'}
                      onChange={(e) => setConfig({ ...config, logo_size: e.target.value })}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500 dark:text-slate-400">px</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Warna Utama
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.primary_color}
                      onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                      className="w-20 h-10 p-1"
                    />
                    <Input
                      type="text"
                      value={config.primary_color}
                      onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                      placeholder="#667eea"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Warna Sekunder
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.secondary_color}
                      onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                      className="w-20 h-10 p-1"
                    />
                    <Input
                      type="text"
                      value={config.secondary_color}
                      onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                      placeholder="#764ba2"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 p-6 rounded-lg" style={{
                background: `linear-gradient(135deg, ${config.primary_color} 0%, ${config.secondary_color} 100%)`
              }}>
                <p className="text-white text-center font-semibold">Preview Gradient Background</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview & Testing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-6 text-center dark:bg-slate-900">
                <p className="text-sm text-gray-600 mb-4 dark:text-slate-300">
                  Halaman isolir di-serve di port 80 (HTTP)
                </p>
                
                <Button
                  onClick={openPreview}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Buka Preview di Tab Baru
                </Button>
                
                <p className="text-xs text-gray-500 mt-3 dark:text-slate-400">
                  Preview URL akan dibuka sesuai tenant Anda
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default IsolirPageEditor;